/**
 * form-submission.js
 *
 * Responsible for slide 1's "next" action:
 *  - Validates all fields when the submit button is clicked.
 *  - If validation passes, advances to the code-verification slide.
 *
 * The actual API call lives in code-verification.js and fires only after
 * the user successfully enters the correct verification code.
 */

import { FIELD_CONFIG } from './field-config';
import { setError, toggleLoader } from './form-fields';
import { saveCurrentStep } from './form-persistence';
import { playSuccessAnimation } from './success-animation';

/**
 * Validate all fields defined in FIELD_CONFIG.
 * Returns true if all pass, false if any fail.
 *
 * @param {Record<string, HTMLElement>} elements
 * @returns {boolean}
 */
export function validateAllFields(elements) {
	let isValid = true;

	Object.entries(FIELD_CONFIG).forEach(([name, config]) => {
		if (!config.validate) return;

		const el = elements[name];
		const val = config.type === 'checkbox' ? el.checked : el.value;
		const err = config.validate(val);

		if (err) {
			setError(el, err);
			isValid = false;
		}
	});

	return isValid;
}

/**
 * Attach the slide-1 submit button click handler.
 * Validates all fields; if valid, calls the API to send the initial code
 * and then advances the swiper to slide 2.
 *
 * @param {HTMLFormElement}             form
 * @param {Record<string, HTMLElement>} elements - Field name → DOM element map from initFormFields().
 * @param {import('swiper').default}    swiper
 * @param {import('./api').default}     api
 */
export function initFormSubmission(form, elements, swiper, api) {
	const submitBtn = form.querySelector('.cr-submit-btn');
	const statusMsg = form.querySelector('.cr-form__status-msg');

	submitBtn?.addEventListener('click', async () => {
		// Animation test
		// const replayButton = document.querySelector('.replay-animation');
		// replayButton.addEventListener('click', playSuccessAnimation);
		// playSuccessAnimation();

		if (swiper && swiper.activeIndex !== 0) return;
		statusMsg.textContent = '';

		if (validateAllFields(elements)) {
			submitBtn.disabled = true;
			toggleLoader(form, true);

			try {
				const phone = '370' + elements.phone.value.replace(/\s/g, '');
				const name = elements.name.value.trim();
				await api.sendCode(phone, name);
				swiper?.slideNext();
				saveCurrentStep(1);
			} catch (err) {
				console.error(
					'[Card Registration] Initial send failed:',
					err.message
				);
				statusMsg.textContent =
					err.message || 'Įvyko klaida. Bandykite vėliau.';
				statusMsg.classList.add('cr-form__status-msg--error');
			} finally {
				submitBtn.disabled = false;
				toggleLoader(form, false);
			}
		}
	});
}
